// src/components/auth/BootGate.tsx
// Root boot gate — wraps ENTIRE app shell.
// Nothing protected renders until Central switch-context returns 200.
//
// FOUNDATION INTEGRATION:
// - Calls evaluateFeatureFlags after setBootReady (fire-and-forget)
// - Mounts useCentralEventStream when boot is ready
// - Feature flag evaluation NEVER blocks boot

import { createContext, useContext, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  selectCentralAccessToken,
  centralLogout,
  syncCentralUserFromBoot,
} from '../../store/slices/centralAuthSlice';
import {
  setBootLoading,
  setBootReady,
  setBootWorkspaceClosed,
  setBootUnavailable,
  setBootUnauthenticated,
  selectBootStatus,
} from '../../store/slices/bootContextSlice';
import {
  fetchSwitchContext,
  WorkspaceClosedError,
  UnauthorizedError,
  CentralUnavailableError,
} from '../../api/centralSuiteApi';
import { AUTH_MODE } from '../../config/centralAuth';
import { baseApi } from '../../api/baseApi';
import { evaluateFeatureFlags } from '../../api/centralFeaturesApi';
import { useCentralEventStream } from '../../hooks/useCentralEventStream';
import WorkspaceClosedOverlay from './WorkspaceClosedOverlay';
import CentralUnavailableOverlay from './CentralUnavailableOverlay';
import LoadingSpinner from '../common/LoadingSpinner';

// ============================================================
// BootGate Context — exposes rerunBoot() for workspace switch + retry
// ============================================================

interface BootGateContextValue {
  rerunBoot: () => void;
}

const BootGateContext = createContext<BootGateContextValue>({
  rerunBoot: () => {},
});

export function useBootGate() {
  return useContext(BootGateContext);
}

// ============================================================
// BootGate Component
// ============================================================

interface BootGateProps {
  children: ReactNode;
}

export default function BootGate({ children }: BootGateProps) {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(selectCentralAccessToken);
  const bootStatus = useAppSelector(selectBootStatus);

  // Track boot trigger count for re-runs
  const bootTrigger = useRef(0);

  const runBoot = useCallback(async () => {
    // Legacy mode: pass through immediately
    if (AUTH_MODE === 'legacy') return;

    // No token: pass through (RequireAuth will redirect to /login)
    if (!accessToken) return;

    const currentTrigger = ++bootTrigger.current;

    // Presence, never material. See centralSuiteApi for why a prefix is not
    // a mitigation.
    console.log('[BootGate] runBoot starting, token present:', accessToken.length > 0);

    dispatch(setBootLoading());

    try {
      const response = await fetchSwitchContext(accessToken);

      // Stale check: if rerunBoot was called again while we were awaiting, discard this result
      if (currentTrigger !== bootTrigger.current) return;

      console.log('[BootGate] switch-context SUCCESS:', {
        currentWorkspace: response.currentWorkspace?.name,
        user: response.user?.email,
        apps: response.apps?.length,
      });

      // Boot is complete — unblock UI immediately
      dispatch(setBootReady(response));

      // Sync user data from switch-context to centralAuth to keep both sources consistent.
      // This ensures Settings page (reads centralAuth.user) matches shell (reads bootContext.user).
      if (response.user) {
        dispatch(syncCentralUserFromBoot(response.user));
      }

      // FOUNDATION: Feature flag evaluation is fire-and-forget.
      // INVARIANT: This NEVER blocks boot. Failures default all flags to false.
      if (response.currentWorkspace?.id) {
        evaluateFeatureFlags(accessToken, response.currentWorkspace.id, dispatch);
      }
    } catch (err) {
      // Stale check
      if (currentTrigger !== bootTrigger.current) return;

      console.error('[BootGate] switch-context FAILED:', {
        errorType: err instanceof Error ? err.constructor.name : typeof err,
        message: err instanceof Error ? err.message : String(err),
      });

      if (err instanceof WorkspaceClosedError) {
        // Clear all caches immediately
        dispatch(baseApi.util.resetApiState());
        dispatch(
          setBootWorkspaceClosed({ otherWorkspaces: err.otherWorkspaces })
        );
      } else if (err instanceof UnauthorizedError) {
        dispatch(centralLogout());
        dispatch(setBootUnauthenticated());
      } else if (err instanceof CentralUnavailableError) {
        dispatch(setBootUnavailable(err.message));
      } else {
        dispatch(
          setBootUnavailable(
            err instanceof Error ? err.message : 'Unknown error'
          )
        );
      }
    }
  }, [accessToken, dispatch]);

  // Re-run boot on initial mount and when accessToken changes
  useEffect(() => {
    runBoot();
  }, [runBoot]);

  const rerunBoot = useCallback(() => {
    runBoot();
  }, [runBoot]);

  const contextValue: BootGateContextValue = { rerunBoot };

  // Legacy mode: pass through
  if (AUTH_MODE === 'legacy') {
    return (
      <BootGateContext.Provider value={contextValue}>
        {children}
      </BootGateContext.Provider>
    );
  }

  // No token: pass through (RequireAuth handles redirect)
  if (!accessToken) {
    return (
      <BootGateContext.Provider value={contextValue}>
        {children}
      </BootGateContext.Provider>
    );
  }

  // Loading state
  if (bootStatus === 'idle' || bootStatus === 'loading') {
    return (
      <BootGateContext.Provider value={contextValue}>
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-sm text-gray-500">Connecting to Central...</p>
        </div>
      </BootGateContext.Provider>
    );
  }

  // Workspace closed — non-dismissible overlay, children do NOT render
  if (bootStatus === 'workspace_closed') {
    return (
      <BootGateContext.Provider value={contextValue}>
        <WorkspaceClosedOverlay />
      </BootGateContext.Provider>
    );
  }

  // Central unavailable — non-dismissible overlay, children do NOT render
  if (bootStatus === 'unavailable') {
    return (
      <BootGateContext.Provider value={contextValue}>
        <CentralUnavailableOverlay />
      </BootGateContext.Provider>
    );
  }

  // Unauthenticated — pass through so RequireAuth can redirect to /login
  if (bootStatus === 'unauthenticated') {
    return (
      <BootGateContext.Provider value={contextValue}>
        {children}
      </BootGateContext.Provider>
    );
  }

  // Ready — render children with SSE connection
  return (
    <BootGateContext.Provider value={contextValue}>
      <BootGateContent>{children}</BootGateContent>
    </BootGateContext.Provider>
  );
}

// ============================================================
// BootGateContent — SSE Integration
// ============================================================

/**
 * Wrapper component that mounts SSE hook when boot is ready.
 * Must be inside BootGate (after boot is ready) for SSE to connect.
 */
function BootGateContent({ children }: { children: ReactNode }) {
  // FOUNDATION: Connect to Central SSE event stream.
  // Hook handles lifecycle: connect, disconnect, reconnect with backoff.
  useCentralEventStream();

  return <>{children}</>;
}
